/**
 * Peta dasar — aritmetika bersama layar Field Map dan basemap korelasi.
 *
 * Transformasi dunia→layar dan kontur struktur dulu hidup di dalam
 * `screens/FieldMap.tsx`. Begitu layar korelasi menggambar peta yang sama,
 * dua salinan aritmetika yang sama berarti dua peta yang boleh berbeda tanpa
 * salah satunya salah — jadi ia pindah ke sini, tempat ia bisa diuji tanpa
 * menyalakan React.
 */

export interface Extent {
  min_x: number;
  max_x: number;
  min_y: number;
  max_y: number;
}

export interface MapTransform {
  /** Piksel layar per satu satuan peta (meter). */
  scale: number;
  toScreen: (x: number, y: number) => [number, number];
  toWorld: (px: number, py: number) => [number, number];
}

/** Titik permukaan yang dipakai sebagai kendali kontur, z dalam TVDSS. */
export interface ControlPoint {
  x: number;
  y: number;
  z: number;
}

export interface ContourLine {
  level: number;
  interval: number;
  /** Ruas-ruas kontur sebagai satu atribut `d` path SVG, koordinat layar. */
  d: string;
  opacity: number;
}

/** Bentuk minimum sebuah sumur bagi peta: kepala sumur dan markernya. */
export interface MappableWell {
  x: number | null;
  y: number | null;
  kb_elevation: number | null;
  markers: { name: string; md: number }[];
  /**
   * Tebal net pay sumur ini, meter — dari run deterministik terakhirnya.
   *
   * Kosong berarti **belum pernah dihitung**, dan itu berbeda dari nol. Nol
   * adalah sebuah pengukuran: "sudah diperiksa, tidak ada net pay". Sumur yang
   * belum dihitung tidak mengatakan itu.
   */
  net_pay?: number | null;
  /**
   * Net pay tiap zona sumur ini, meter. Aturan yang sama berlaku per zona:
   * zona yang tidak disebut adalah zona yang tidak masuk hitungan sumur itu,
   * bukan zona setebal nol.
   */
  net_pay_zones?: Record<string, number> | null;
}

/** Interval kontur yang tersedia, meter. */
export const CONTOUR_STEPS = [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500];

/** Warna simbol menurut status sumur — sama di Field Map dan basemap korelasi. */
export const WELL_STATUS_LEGEND: { name: string; fill: string }[] = [
  { name: "Oil producer", fill: "#7fc06a" },
  { name: "Gas producer", fill: "#d69a4a" },
  { name: "Dry / abandoned", fill: "#8b939e" },
  { name: "Suspended", fill: "#9b8ad6" },
  { name: "Planned", fill: "none" },
];

/**
 * Kotak pembatas kepala sumur, dilebarkan `padFraction` di tiap sisi.
 *
 * Sumur tanpa koordinat diabaikan, dan lapangan bersumur tunggal — yang
 * rentangnya nol — tetap mendapat kotak selebar `fallbackPad` agar peta
 * punya sesuatu untuk diskalakan.
 */
export function extentOf(
  wells: { x: number | null; y: number | null }[],
  padFraction = 0.18,
  fallbackPad = 1000,
): Extent | null {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const well of wells) {
    if (well.x === null || well.y === null) continue;
    if (!Number.isFinite(well.x) || !Number.isFinite(well.y)) continue;
    xs.push(well.x);
    ys.push(well.y);
  }
  if (!xs.length) return null;
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const padX = (maxX - minX) * padFraction || fallbackPad;
  const padY = (maxY - minY) * padFraction || fallbackPad;
  return {
    min_x: minX - padX,
    max_x: maxX + padX,
    min_y: minY - padY,
    max_y: maxY + padY,
  };
}

/**
 * Peta dunia → layar, satu skala untuk kedua sumbu.
 *
 * Skala yang berbeda per sumbu membuat lintasan lurus tampak membengkok, dan
 * itu justru pertanyaan yang dijawab peta ini.
 */
export function fitTransform(
  extent: Extent | null | undefined,
  width: number,
  height: number,
  pad = 44,
): MapTransform | null {
  if (!extent || width <= 0 || height <= 0) return null;
  const { min_x, max_x, min_y, max_y } = extent;
  const spanX = max_x - min_x || 1;
  const spanY = max_y - min_y || 1;
  const scale = Math.min((width - pad * 2) / spanX, (height - pad * 2) / spanY);
  if (!Number.isFinite(scale) || scale <= 0) return null;
  const offsetX = (width - spanX * scale) / 2;
  const offsetY = (height - spanY * scale) / 2;
  return {
    scale,
    toScreen: (x, y) => [
      offsetX + (x - min_x) * scale,
      // Northing naik ke atas; y layar naik ke bawah.
      offsetY + (max_y - y) * scale,
    ],
    toWorld: (px, py) => [
      min_x + (px - offsetX) / scale,
      max_y - (py - offsetY) / scale,
    ],
  };
}

/**
 * Interval bulat yang menghasilkan kira-kira `count` garis atas relief nyata.
 *
 * Interval tetap 50 m menarik dua garis di struktur landai dan seratus garis
 * di struktur curam.
 */
export function contourInterval(zMin: number, zMax: number, count = 8): number {
  const target = Math.max((zMax - zMin) / count, 1);
  return CONTOUR_STEPS.find((step) => step >= target) ?? 500;
}

/** Ketinggian kontur dari `zMin` ke `zMax` pada kelipatan `interval`. */
export function contourLevels(zMin: number, zMax: number, interval: number): number[] {
  const levels: number[] = [];
  if (!(interval > 0) || !Number.isFinite(zMin) || !Number.isFinite(zMax)) return levels;
  for (let level = Math.ceil(zMin / interval) * interval; level <= zMax; level += interval) {
    levels.push(level);
  }
  return levels;
}

/**
 * Titik kendali kontur: kepala sumur berkoordinat yang menembus `markerName`.
 *
 * z adalah TVDSS, negatif ke bawah, jadi kontur bernomor besar berarti tinggi
 * struktur — konvensi yang sama dengan peta struktur cetak.
 */
export function structureControlPoints(
  wells: MappableWell[],
  markerName: string,
): ControlPoint[] {
  if (!markerName) return [];
  const points: ControlPoint[] = [];
  for (const well of wells) {
    if (well.x === null || well.y === null || well.kb_elevation === null) continue;
    const marker = well.markers.find((m) => m.name === markerName);
    if (!marker) continue;
    points.push({ x: well.x, y: well.y, z: -(marker.md - well.kb_elevation) });
  }
  return points;
}

/**
 * Titik kendali peta net pay: kepala sumur berkoordinat yang sudah dihitung.
 *
 * `zone` kosong berarti net pay total sumur — jumlah seluruh zona yang
 * ditembusnya; sebuah nama zona berarti zona itu saja. Keduanya peta yang
 * berbeda, dan yang digambar orang hampir selalu yang kedua: total sebuah
 * sumur ikut turun ketika bornya berhenti di atas reservoir berikutnya, jadi
 * peta total melukiskan kedalaman TD sebanyak ia melukiskan batuan.
 *
 * Sumur yang belum punya hasil hitungan **dibuang**, bukan dianggap nol —
 * begitu pula sumur yang hitungannya tidak memuat zona yang diminta.
 * Perbedaannya menentukan bentuk petanya: sebuah sumur yang belum diperiksa
 * siapa pun, ditaruh sebagai nol, akan menarik kontur di sekitarnya turun dan
 * melukiskan lubang yang tidak pernah diukur — dan lubang itu tampak persis
 * seperti temuan geologi.
 *
 * z di sini adalah ketebalan, positif ke atas, jadi kontur bernomor besar
 * berarti net pay tebal — kebalikan arah dari peta struktur, tempat z adalah
 * TVDSS yang negatif ke bawah.
 */
export function netPayControlPoints(wells: MappableWell[], zone = ""): ControlPoint[] {
  const points: ControlPoint[] = [];
  for (const well of wells) {
    if (well.x === null || well.y === null) continue;
    const pay = zone ? well.net_pay_zones?.[zone] : well.net_pay;
    if (pay === null || pay === undefined || !Number.isFinite(pay)) continue;
    points.push({ x: well.x, y: well.y, z: pay });
  }
  return points;
}

/**
 * Kontur struktur dari kedalaman marker.
 *
 * Dengan sumur segelintir, pembobotan jarak terbalik di atas kisi kasar lalu
 * penjejakan marching-squares memberi gambaran struktur yang jujur — halus,
 * tetapi berasal dari data marker yang sama dengan yang disunting layar
 * korelasi. Kurang dari tiga titik kendali bukan permukaan, jadi hasilnya
 * kosong.
 */
export function structureContours(
  control: ControlPoint[],
  transform: MapTransform | null,
  width: number,
  height: number,
  cols = 56,
  rows = 40,
): ContourLine[] {
  if (!transform || control.length < 3 || width <= 0 || height <= 0) return [];

  const zs = control.map((p) => p.z);
  const zMin = Math.min(...zs);
  const zMax = Math.max(...zs);
  const interval = contourInterval(zMin, zMax);
  const levels = contourLevels(zMin, zMax, interval);
  if (!levels.length) return [];

  const grid: number[][] = [];
  for (let row = 0; row <= rows; row++) {
    grid[row] = [];
    for (let col = 0; col <= cols; col++) {
      const px = (col / cols) * width;
      const py = (row / rows) * height;
      const [wx, wy] = transform.toWorld(px, py);
      let weighted = 0;
      let weights = 0;
      for (const point of control) {
        const distance = Math.hypot(point.x - wx, point.y - wy);
        if (distance < 1) {
          weighted = point.z;
          weights = 1;
          break;
        }
        const weight = 1 / (distance * distance);
        weighted += point.z * weight;
        weights += weight;
      }
      grid[row][col] = weights ? weighted / weights : zMin;
    }
  }

  // Marching squares, ruas tepi saja — cukup untuk sebuah peta kontur.
  return levels.map((level, index) => {
    const segments: string[] = [];
    const interpolate = (a: number, b: number) => (level - a) / (b - a || 1);
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x0 = (col / cols) * width;
        const x1 = ((col + 1) / cols) * width;
        const y0 = (row / rows) * height;
        const y1 = ((row + 1) / rows) * height;
        const tl = grid[row][col];
        const tr = grid[row][col + 1];
        const br = grid[row + 1][col + 1];
        const bl = grid[row + 1][col];
        const points: [number, number][] = [];
        if (tl < level !== tr < level) points.push([x0 + (x1 - x0) * interpolate(tl, tr), y0]);
        if (tr < level !== br < level) points.push([x1, y0 + (y1 - y0) * interpolate(tr, br)]);
        if (br < level !== bl < level) points.push([x0 + (x1 - x0) * interpolate(bl, br), y1]);
        if (bl < level !== tl < level) points.push([x0, y0 + (y1 - y0) * interpolate(tl, bl)]);
        if (points.length >= 2) {
          segments.push(
            `M${points[0][0].toFixed(1)},${points[0][1].toFixed(1)} L${points[1][0].toFixed(1)},${points[1][1].toFixed(1)}`,
          );
        }
      }
    }
    const strength = 0.18 + (index / Math.max(levels.length - 1, 1)) * 0.67;
    return { level, interval, d: segments.join(" "), opacity: strength };
  })
    // Ketinggian yang tidak dipotong satu sel pun bukan sebuah garis. Ia lahir
    // ketika seluruh titik kendali bernilai sama — zona yang net pay-nya nol di
    // mana-mana, misalnya — dan yang dipulangkannya adalah `d` kosong: tak
    // tergambar, tak berlabel, tetapi tetap terhitung. Keterangan yang menyebut
    // "1 garis" di atas peta yang tidak memuat satu garis pun membuat
    // pembacanya mencari garis yang tidak pernah ada.
    .filter((contour) => contour.d.length > 0);
}

/**
 * Titik pertama sebuah kontur, untuk menempelkan angkanya.
 *
 * Kontur yang tak punya satu ruas pun tidak diberi label, bukan diberi label
 * di sudut kiri atas.
 */
export function contourAnchor(contour: ContourLine): [number, number] | null {
  const first = contour.d.split(" ")[0];
  if (!first?.startsWith("M")) return null;
  const [px, py] = first.slice(1).split(",").map(Number);
  if (!Number.isFinite(px) || !Number.isFinite(py)) return null;
  return [px, py];
}

/** Sebuah nama yang menunggu tempat: siapa, tulisannya, dan titiknya di layar. */
export interface LabelCandidate {
  id: number;
  name: string;
  at: [number, number];
}

/**
 * Nama siapa yang muat di peta tanpa menimpa nama tetangganya.
 *
 * Yang menentukan keterbacaan adalah jarak antar tulisan di layar, bukan cacah
 * sumurnya, dan bedanya bukan teori: Jirak memuat 247 sumur di dalam tiga
 * kilometer, jadi pada pandangan yang memuat seluruh Prabumulih ke-247 namanya
 * tercetak di atas satu sama lain dan yang tersisa di layar adalah sebuah
 * gumpalan hitam — bukan satu nama pun yang terbaca, dan bukan pula sumur yang
 * hilang. Aturan berdasarkan cacah ("beri nama kalau yang tergambar di bawah
 * dua belas") tidak pernah menyalakan satu nama pun di sana, bahkan pada
 * perbesaran yang titiknya sudah terpisah seratus piksel.
 *
 * Jadi namanya dipasang serakah, menurut urutan yang diberikan pemanggilnya —
 * yang terpilih lebih dulu, lalu yang sedang dirangkai menjadi lintasan — dan
 * sebuah nama dilewati ketika kotaknya bertabrakan dengan nama yang sudah
 * berdiri. Memperbesar merenggangkan titiknya, dan nama muncul sendiri satu per
 * satu tanpa sakelar yang harus ditemukan lebih dulu.
 *
 * Yang disembunyikan hanya **tulisannya**. Titik sumurnya tetap digambar
 * seluruhnya: peta yang membuang sumur demi kerapian adalah peta yang berbohong
 * tentang lapangannya.
 *
 * Kotak, bukan lingkaran: tulisannya panjang dan pendek, jadi dua nama yang
 * bersebelahan mendatar bertabrakan jauh sebelum dua nama yang bersusun tegak.
 */
export function placeLabels(
  candidates: LabelCandidate[],
  view: { width: number; height: number },
  { glyph = 6, offset = 14, height = 15, limit = 60 } = {},
): Set<number> {
  const boxes: { x0: number; y0: number; x1: number; y1: number }[] = [];
  const out = new Set<number>();
  for (const row of candidates) {
    // Batas atas yang murah: enam puluh nama sudah lebih banyak daripada yang
    // dibaca siapa pun sekali pandang, dan yang di atasnya hanya menambah
    // simpul DOM di dalam bingkai yang lebarnya tetap.
    if (out.size >= limit) break;
    const [px, py] = row.at;
    // Yang jatuh di luar bingkai tidak ikut merebut tempat. Tanpa ini sebuah
    // peta yang digeser menghabiskan jatah namanya pada sumur yang tidak
    // terlihat, dan yang di tengah layar justru tinggal titik.
    if (px < -40 || px > view.width + 40 || py < -20 || py > view.height + 20) continue;
    const box = {
      x0: px + offset,
      y0: py - height / 2,
      x1: px + offset + 12 + row.name.length * glyph,
      y1: py + height / 2,
    };
    if (boxes.some((b) => box.x0 < b.x1 && box.x1 > b.x0 && box.y0 < b.y1 && box.y1 > b.y0)) {
      continue;
    }
    boxes.push(box);
    out.add(row.id);
  }
  return out;
}

/**
 * Zoom dan geseran sebuah peta, dalam piksel layar.
 *
 * Ia berdiri terpisah dari `fitTransform` supaya "peta apa yang muat" dan
 * "bagian mana yang sedang dilihat" tetap dua pertanyaan berbeda: batas peta
 * ditentukan sebaran sumur dan tidak boleh berubah saat pengguna memperbesar,
 * sedangkan pandangan ini milik mata yang sedang bekerja dan tidak boleh
 * mengubah skala batang atau kontur.
 */
export interface MapView {
  /** 1 berarti seluruh peta muat, seperti sebelum ada zoom. */
  zoom: number;
  /** Geseran layar, piksel. */
  x: number;
  y: number;
}

/** Pandangan awal: pas di kotak, tanpa geseran. */
export const MAP_HOME: MapView = { zoom: 1, x: 0, y: 0 };

export const MAP_ZOOM_MIN = 0.5;
export const MAP_ZOOM_MAX = 16;

export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return 1;
  return Math.min(MAP_ZOOM_MAX, Math.max(MAP_ZOOM_MIN, zoom));
}

/**
 * Memperbesar `factor` kali di sekitar satu titik layar.
 *
 * Titik di bawah kursor harus tetap menunjuk tempat yang sama di dunia.
 * Zoom yang selalu berpusat di tengah kotak membuat pengguna mengejar
 * targetnya: ia memperbesar, yang dicarinya terlempar keluar layar, lalu ia
 * menggeser kembali. Karena itu jangkarnya adalah kursor.
 */
export function zoomAt(view: MapView, factor: number, ax: number, ay: number): MapView {
  const zoom = clampZoom(view.zoom * factor);
  // Faktor sesungguhnya dihitung setelah penjepitan, jika tidak geseran ikut
  // bergerak saat zoom sudah mentok dan peta melayang tanpa membesar.
  const applied = zoom / view.zoom;
  return {
    zoom,
    x: ax - (ax - view.x) * applied,
    y: ay - (ay - view.y) * applied,
  };
}

/** Menggeser pandangan sejauh `dx`, `dy` piksel layar. */
export function panBy(view: MapView, dx: number, dy: number): MapView {
  return { ...view, x: view.x + dx, y: view.y + dy };
}

/** Benar bila pandangan masih persis seperti `MAP_HOME`. */
export function isHome(view: MapView): boolean {
  return view.zoom === 1 && view.x === 0 && view.y === 0;
}

/**
 * Menyusun pandangan di atas transformasi peta dasar.
 *
 * `scale` ikut dikalikan supaya batang skala tetap jujur: memperbesar dua kali
 * berarti satu kilometer menempati dua kali lebih banyak piksel, dan batang
 * yang tidak ikut berubah akan berbohong tentang jarak.
 */
export function applyView(
  base: MapTransform | null | undefined,
  view: MapView,
): MapTransform | null {
  if (!base) return null;
  if (isHome(view)) return base;
  const { zoom, x, y } = view;
  return {
    scale: base.scale * zoom,
    toScreen: (wx, wy) => {
      const [bx, by] = base.toScreen(wx, wy);
      return [bx * zoom + x, by * zoom + y];
    },
    toWorld: (px, py) => base.toWorld((px - x) / zoom, (py - y) / zoom),
  };
}

/**
 * `viewBox` sebuah SVG yang ruang koordinatnya sendiri 0..`w` × 0..`h`.
 *
 * Untuk peta yang digambar dalam koordinat abstrak — model geologi menggambar
 * di kisi 1000 × 1000 — memindahkan `viewBox` lebih murah daripada menghitung
 * ulang setiap persegi, dan hasilnya sama persis dengan `applyView`.
 */
export function viewBoxFor(view: MapView, w: number, h: number): string {
  const { zoom, x, y } = view;
  return `${(-x / zoom).toFixed(2)} ${(-y / zoom).toFixed(2)} ${(w / zoom).toFixed(2)} ${(h / zoom).toFixed(2)}`;
}

/**
 * Pemetaan elemen → `viewBox` untuk `preserveAspectRatio="xMidYMid meet"`.
 *
 * SVG yang dipasangi "meet" menyisakan pita kosong di sisi yang berlebih, dan
 * kursor yang jatuh di piksel ke-40 elemen tidak jatuh di unit ke-40 viewBox.
 * Tanpa koreksi ini, zoom berjangkar kursor meleset persis sebesar pita itu.
 */
export function meetMapping(
  boxW: number,
  boxH: number,
  viewW: number,
  viewH: number,
): { scale: number; offsetX: number; offsetY: number } {
  const scale = Math.min(boxW / viewW, boxH / viewH) || 1;
  return {
    scale,
    offsetX: (boxW - viewW * scale) / 2,
    offsetY: (boxH - viewH * scale) / 2,
  };
}

/**
 * Kotak yang benar-benar terlihat, dalam satuan gambar peta.
 *
 * Isinya sama persis dengan yang dikatakan `viewBoxFor` kepada SVG, tetapi
 * sebagai angka yang bisa dipakai menempatkan hiasan peta — sumbu koordinat,
 * batang skala — di tepi yang sedang tampak, bukan di tepi kisi yang mungkin
 * sudah lama tergeser ke luar layar. Keduanya harus tetap satu hitungan:
 * hiasan yang menghitung sendiri kotaknya boleh meleset dari petanya tanpa
 * salah satunya terlihat salah.
 *
 * `k` mengembalikan ukuran layar sebuah unsur. Pada zoom empat kali, sesuatu
 * yang digambar setebal `4k` satuan tetap setebal empat piksel — angka sumbu
 * dan simbol sumur adalah perkakas untuk membaca peta, bukan isi petanya, dan
 * yang ikut membesar empat kali berhenti bisa dibaca.
 */
export function seenBox(
  view: MapView,
  width: number,
  height: number,
): { left: number; top: number; width: number; height: number; k: number } {
  const k = 1 / view.zoom;
  return { left: -view.x * k, top: -view.y * k, width: width * k, height: height * k, k };
}

/** Huruf ujung sebuah lintasan, dan sepasang huruf itu sebagai satu label. */
export interface TraverseEnds {
  /** Ujung pangkal, digambar di sumur pertama panel. */
  start: string;
  /** Ujung seberang, beserta tanda aksennya. */
  end: string;
  /** Keduanya sebagaimana tertulis pada nama lintasan: "B–B′". */
  label: string;
}

/** A–A′ tanpa aksen, dan A–A′ utuh: ujung lintasan yang tak bernama. */
const DEFAULT_ENDS: TraverseEnds = { start: "A", end: "A′", label: "A–A′" };

/**
 * Sepasang huruf ujung di dalam nama lintasan: `A–A′`, `B–B′`, `C-C'`.
 *
 * Hurufnya harus sama di kedua sisi dan sisi kanan harus beraksen — tanpa
 * kedua syarat itu "Lintasan Troll–Brage" ikut terbaca sebagai sepasang ujung,
 * dan petanya akan menamai ujungnya "Troll" dan "Brage".
 */
const ENDS_IN_NAME =
  /(?:^|[\s(])([A-Za-z0-9]{1,3})\s*([–—-])\s*\1\s*([′'’])/;

/**
 * Huruf ujung sebuah lintasan, dibaca dari namanya sendiri.
 *
 * Peta dulu menuliskan "A", "A′", dan "A–A′" sebagai literal, jadi lintasan
 * B–B′ pun tergambar sebagai A–A′ — keterangan yang salah dan tidak pernah
 * mengeluh. Nama yang tidak menyebut sepasang ujung tetap memakai A–A′: itu
 * penamaan lazim bagi ujung sebuah penampang, dan lintasan bertanggal tidak
 * punya huruf yang bisa dipakai.
 */
export function traverseEnds(name?: string | null): TraverseEnds {
  const m = name ? ENDS_IN_NAME.exec(name) : null;
  if (!m) return DEFAULT_ENDS;
  const [, letter, dash, prime] = m;
  return { start: letter, end: letter + prime, label: letter + dash + letter + prime };
}
